import type {
  AnswerInput,
  AnswerOption,
  AnswerResponse,
  PreviewResult,
  Question,
  QuestionOptionItem,
} from '@tabel/shared/schemas/questions';
import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Clock,
  MessageCircleQuestion,
  PenLine,
  RefreshCw,
  TriangleAlert,
  Undo2,
} from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { api } from '../lib/api';
import { ApiError } from '../lib/errors';
import { type HoursFormat, hoursText, ruDate, ruDateTime } from '../lib/format';
import {
  answerOptions,
  answerSummary,
  isUnanswered,
  parseTime,
  plural,
  questionText,
  staleQuestions,
  typeKind,
  typeLabel,
  typeTone,
  warningText,
} from '../lib/questions';
import { hoursHint, parseHours } from './CellEditForm';
import { errorText, useToast } from './ErrorToast';
import { UnsavedMark, useUnsavedChanges, useUnsavedGuard } from './UnsavedGuard';
import { Badge, Button, Callout, textareaCls, useNarrowScreen } from './ui';

/*
 * Вопрос бригадиру в карточке дня (003, US2; FR-015–FR-020, FR-025, FR-029; contracts/ui-panel.md, «Карточка дня:
 * состав», «Подвал»). Вопрос — простыми словами с фактами дня; варианты — от сервера по типу вопроса (радио, клавиши
 * 1–4 и Enter на компьютере); поля часов и пояснения (до 300 знаков); «В табель попадёт» — предпросмотр сервера через
 * 300 мс после ввода (R6): ошибки ввода блокируют «Сохранить», предупреждения — нет. Ответ — несохранённое
 * (`UnsavedGuard`). Часы считает только сервер: интерфейс их показывает.
 * Ответ временем (Q-27, T071; FR-016): у «нет выхода» и «нет входа» — поле времени; «19:00», «1900», «19», «7:5»
 * приводятся к `ЧЧ:ММ` до запроса, неразборчивое — подсказка под полем без запроса. Часы, строки чека и отрезок для
 * полосы дня считает сервер расчётом «что если»; смена длиннее 16 ч — предупреждение, сохранить можно.
 */

const SCREEN = 'Timesheet';
const PREVIEW_DELAY_MS = 300;

/** Ввод ответа: вариант, часы и время (как введены), пояснение. */
export interface DraftValues {
  option: AnswerOption | null;
  hours: string;
  time: string;
  note: string;
}

type Preview =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; results: PreviewResult[] }
  | { status: 'fail'; error: unknown };

export interface AnswerForm {
  values: DraftValues;
  setOption: (o: AnswerOption) => void;
  setHours: (v: string) => void;
  setTime: (v: string) => void;
  setNote: (v: string) => void;
  /** Ошибка ввода часов (своя проверка, до сервера); показывается, если поле не пустое. */
  hoursError: string | null;
  /** Время ответа: приведённое к `ЧЧ:ММ` или что исправить; поле пустое — `null`. */
  timeParsed: { time: string } | { error: string } | null;
  /** Ответ для запроса; ввод не дописан или с ошибкой — `null`. */
  request: AnswerInput | null;
  preview: Preview;
  retryPreview: () => void;
  /** Ответ дописан, предпросмотр без ошибок ввода (у «Уточню позже» предпросмотра нет). */
  ready: boolean;
  /**
   * Ответ дописан и сервер не нашёл в нём ошибок (предпросмотр мог ещё не прийти): так можно сохранить из окна
   * «Сохранить ответ?» — сервер проверит ответ при записи.
   */
  complete: boolean;
  dirty: boolean;
  /** Сбросить ввод к начальному. */
  reset: () => void;
  format: HoursFormat | null;
}

interface FormState {
  key: string;
  values: DraftValues;
  init: DraftValues;
  /** Версия вопроса, с которой открыт ввод (`STALE_STATE` — если она устарела). */
  baseVersion: number;
  /** Сервер ответил «вопрос изменился»: следующая попытка — с текущей версией. */
  stale: boolean;
}

const same = (a: DraftValues, b: DraftValues) =>
  a.option === b.option && a.hours === b.hours && a.time === b.time && a.note === b.note;

/** Ответ для запроса из ввода; не дописан или с ошибкой — `null`. Часы — по формату правил объекта. */
function toRequest(v: DraftValues, format: HoursFormat | null): AnswerInput | null {
  if (!v.option) return null;
  const note = v.note.trim();
  const tail = note ? { note } : {};
  if (v.option === 'hours') {
    const p = parseHours(v.hours, format);
    return 'hours' in p ? { option: 'hours', hours: p.hours, ...tail } : null;
  }
  if (v.option === 'time') {
    // Сервер принимает только `ЧЧ:ММ`: «1900», «19», «7:5» приводятся здесь, неразборчивое не отправляется.
    const t = v.time.trim() ? parseTime(v.time) : null;
    return t && 'time' in t ? { option: 'time', time: t.time, ...tail } : null;
  }
  return { option: v.option, ...tail };
}

/**
 * Ввод ответа на один или несколько вопросов одного типа и предпросмотр сервера (R6). `formKey` — что за ввод:
 * другой ключ — ввод начинается заново с `initial`.
 */
export function useAnswerForm({
  timesheetId,
  formKey,
  items,
  initial,
  format,
  enabled,
}: {
  timesheetId: number;
  formKey: string;
  items: readonly { questionId: number; version: number }[];
  initial: DraftValues;
  format: HoursFormat | null;
  /** Ввод на экране: без него предпросмотр не запрашивается. */
  enabled: boolean;
}): AnswerForm & {
  state: FormState | null;
  setState: (fn: (s: FormState | null) => FormState | null) => void;
} {
  const [state, setRaw] = useState<FormState | null>(null);
  const s = state && state.key === formKey ? state : null;
  const values = s?.values ?? initial;
  const firstVersion = items[0]?.version ?? 0;
  const set = (patch: Partial<DraftValues>) =>
    setRaw((x) => {
      const cur = x && x.key === formKey ? x : null;
      return {
        key: formKey,
        values: { ...(cur?.values ?? initial), ...patch },
        init: cur?.init ?? initial,
        baseVersion: cur?.baseVersion ?? firstVersion,
        stale: cur?.stale ?? false,
      };
    });
  const request = toRequest(values, format);
  const hoursParse =
    values.option === 'hours' && values.hours.trim() ? parseHours(values.hours, format) : null;
  const hoursError = hoursParse && 'error' in hoursParse ? hoursParse.error : null;
  const timeParsed = values.option === 'time' && values.time.trim() ? parseTime(values.time) : null;

  // Предпросмотр: через 300 мс после последнего изменения; ответ на устаревший ввод не показывается.
  const reqKey =
    enabled && request && request.option !== 'later' ? JSON.stringify({ items, answer: request }) : null;
  const [pv, setPv] = useState<{ key: string; preview: Preview } | null>(null);
  const [retry, setRetry] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `retry` — повтор того же запроса
  useEffect(() => {
    if (!reqKey) return;
    let alive = true;
    const t = setTimeout(() => {
      api.previewAnswer(timesheetId, JSON.parse(reqKey)).then(
        (res) => alive && setPv({ key: reqKey, preview: { status: 'ok', results: res.results } }),
        (error: unknown) => alive && setPv({ key: reqKey, preview: { status: 'fail', error } }),
      );
    }, PREVIEW_DELAY_MS);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [reqKey, timesheetId, retry]);
  const preview: Preview = !reqKey
    ? { status: 'idle' }
    : pv && pv.key === reqKey
      ? pv.preview
      : { status: 'loading' };
  const allOk =
    preview.status === 'ok' &&
    items.every((i) => preview.results.find((r) => r.questionId === i.questionId)?.ok === true);
  const ready = request !== null && (request.option === 'later' || allOk);
  const rejected = preview.status === 'ok' && preview.results.some((r) => !r.ok);
  const complete = request !== null && !rejected;

  return {
    values,
    setOption: (option) => set({ option }),
    setHours: (hours) => set({ hours }),
    setTime: (time) => set({ time }),
    setNote: (note) => set({ note }),
    hoursError,
    timeParsed,
    request,
    preview,
    retryPreview: () => {
      setPv(null);
      setRetry((n) => n + 1);
    },
    ready,
    complete,
    dirty: s !== null && !same(s.values, s.init),
    reset: () => setRaw(null),
    format,
    state: s,
    setState: (fn) => setRaw((x) => fn(x && x.key === formKey ? x : null)),
  };
}

/**
 * Начальный ввод: прежний ответ (изменить ответ, «Уточнить», «Уточню позже») или первый вариант. Ответ временем —
 * время бригадира по часам объекта (`tzOffset`).
 */
export function initialValues(q: Question, format: HoursFormat | null, tzOffset = '+05:00'): DraftValues {
  const keys = answerOptions(q).map((o) => o.key);
  const a = q.answer;
  if (a) {
    const option: AnswerOption = a.via === 'edit' ? 'hours' : a.option;
    if (keys.includes(option)) {
      return {
        option,
        hours: option === 'hours' && a.hours != null ? hoursText(a.hours, format) : '',
        time: option === 'time' && a.statedAt ? ruDateTime(a.statedAt, tzOffset).slice(11, 16) : '',
        note: a.note ?? '',
      };
    }
  }
  return { option: keys[0] ?? null, hours: '', time: '', note: '' };
}

const EDITABLE = 'input, textarea, select, [contenteditable="true"]';
/** Enter на этих элементах — их собственное действие, не «Сохранить». */
const OWN_ENTER =
  'textarea, select, a[href], [role="tab"], [role="checkbox"], [role="menuitem"], button:not([role="radio"])';

/**
 * Клавиши ответа на компьютере (FR-025): 1–4 выбирают вариант, Enter сохраняет. Не в поле ввода текста: цифры в
 * полях — это ввод; Enter в пояснении — перенос строки. На телефоне и планшете (до 900 px) клавиш нет.
 */
export function useAnswerKeys({
  enabled,
  options,
  onPick,
  onSave,
}: {
  enabled: boolean;
  options: readonly QuestionOptionItem[];
  onPick: (o: AnswerOption) => void;
  onSave: () => void;
}): boolean {
  const narrow = useNarrowScreen();
  const on = enabled && !narrow;
  const latest = useRef({ options, onPick, onSave });
  latest.current = { options, onPick, onSave };
  useEffect(() => {
    if (!on) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.isComposing) return;
      if (document.querySelector('[role="menu"], dialog[open]')) return; // слой поверх панели
      const t = e.target instanceof Element ? e.target : null;
      if (/^[1-4]$/.test(e.key)) {
        if (t?.closest(EDITABLE)) return;
        const o = latest.current.options[Number(e.key) - 1];
        if (!o) return;
        e.preventDefault();
        latest.current.onPick(o.key);
        if (NEEDS_INPUT.has(o.key)) focusAnswerField();
        return;
      }
      if (e.key === 'Enter') {
        if (t?.closest(OWN_ENTER)) return;
        e.preventDefault();
        latest.current.onSave();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [on]);
  return on;
}

/** Варианты, у которых есть поле ввода: время («Выход в …», «Вход в …») и часы («Указать часы»). */
const NEEDS_INPUT: ReadonlySet<AnswerOption> = new Set<AnswerOption>(['time', 'hours']);

/**
 * Курсор — в поле времени или часов, как только выбран вариант с полем (нажатием или клавишей 1–4): ответ набирается
 * сразу. Поле появляется после выбора — фокус после отрисовки (`setTimeout`, а не кадр: кадры в скрытой вкладке стоят).
 */
export function focusAnswerField(scope: ParentNode = document) {
  setTimeout(() => scope.querySelector<HTMLInputElement>('[data-answer-input]')?.focus(), 0);
}

/**
 * Варианты ответа — радио карточками (дизайн-система, Choice): подпись, подсказка, клавиша на компьютере. Шире 448 px
 * (панель на компьютере и планшете) — по два в ряд, чтобы ответ не уходил вниз; на телефоне — один под другим.
 */
export function AnswerOptions({
  options,
  value,
  onChange,
  keys,
  labelOf,
  disabled,
}: {
  options: readonly QuestionOptionItem[];
  value: AnswerOption | null;
  onChange: (o: AnswerOption) => void;
  /** Показать клавиши 1–4 (компьютер). */
  keys: boolean;
  /** Подпись варианта вместо серверной (ответ сразу нескольким). */
  labelOf?: (o: QuestionOptionItem) => string;
  disabled?: boolean;
}) {
  const root = useRef<HTMLDivElement>(null);
  const pick = (key: AnswerOption) => {
    onChange(key);
    if (NEEDS_INPUT.has(key)) focusAnswerField(root.current?.parentElement ?? document);
  };
  return (
    <div ref={root} className="@container">
      <div role="radiogroup" aria-label="Ответ" className="grid grid-cols-1 gap-2 @md:grid-cols-2">
        {options.map((o, i) => {
          const on = o.key === value;
          return (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={disabled}
              onClick={() => pick(o.key)}
              className={`grid min-h-10 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-0.5 rounded-control border px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                on
                  ? 'border-accent bg-accent-soft'
                  : 'border-line bg-surface hover:border-line-strong hover:bg-subtle'
              }`}
            >
              <span
                aria-hidden
                className={`inline-flex h-4 w-4 items-center justify-center rounded-full border bg-surface ${on ? 'border-accent' : 'border-line-strong'}`}
              >
                {on && <span className="h-2 w-2 rounded-full bg-accent" />}
              </span>
              <span data-label className="min-w-0 text-[14px] font-medium text-ink">
                {labelOf ? labelOf(o) : o.label}
              </span>
              {keys && i < 4 ? (
                <kbd
                  aria-hidden
                  className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-xs border border-b-[1.5px] border-line bg-surface px-1 font-sans text-[11px] font-medium text-ink-2"
                >
                  {i + 1}
                </kbd>
              ) : (
                <span aria-hidden />
              )}
              {o.hint && <span className="col-start-2 text-[12.5px] text-ink-2">{o.hint}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Поле часов и времени: в панели до 900 px — не ниже 40 px (T103), на телефоне — шрифт 16 px. */
const FIELD =
  'h-9 w-full rounded-control border border-line bg-surface px-3 text-[14px] text-ink tabular-nums transition-colors hover:border-line-strong focus:border-accent focus:ring-2 focus:ring-accent/15 focus:outline-none max-sm:h-10 max-sm:text-base max-tablet:min-h-10';

/**
 * Поле времени ответа (Q-27): «19:00», «1900», «19», «7:5». Под полем — что будет записано («Будет записано: 19:00»)
 * или что исправить; неразборчивое на сервер не уходит. На телефоне — цифровая клавиатура: «1900» без двоеточия.
 */
function TimeField({ form, label, hint }: { form: AnswerForm; label: string; hint: string }) {
  const id = useId();
  const p = form.timeParsed;
  const error = p && 'error' in p ? p.error : null;
  const raw = form.values.time.trim();
  const note = error ?? (p && 'time' in p && p.time !== raw ? `Будет записано: ${p.time}` : hint);
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        className={FIELD}
        inputMode="numeric"
        autoComplete="off"
        maxLength={5}
        data-answer-input
        value={form.values.time}
        aria-invalid={error ? true : undefined}
        aria-describedby={`${id}-hint`}
        onChange={(e) => form.setTime(e.target.value)}
      />
      <span id={`${id}-hint`} className={`mt-1 block text-[12px] ${error ? 'text-bad' : 'text-ink-3'}`}>
        {note}
      </span>
    </div>
  );
}

/** Подпись поля времени по типу вопроса: «нет входа» — время входа, иначе — время выхода. */
export const timeLabelOf = (type: string, all = false) =>
  `${typeKind(type) === 'нет_входа' ? 'Время входа' : 'Время выхода'}${all ? ' у всех' : ''}`;

/** Поля варианта: часы (у «Указать часы»), время (у «Выход в …», «Вход в …») и пояснение до 300 знаков. */
export function AnswerFields({
  form,
  hoursLabel = 'Часы',
  timeLabel = 'Время выхода',
  timeHint = 'Например, 19:00 или 1900',
  notePlaceholder = 'Например: выход вместе с бригадой после разгрузки',
}: {
  form: AnswerForm;
  hoursLabel?: string;
  timeLabel?: string;
  timeHint?: string;
  notePlaceholder?: string;
}) {
  const hoursId = useId();
  const noteId = useId();
  const v = form.values;
  return (
    <div className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-x-3 gap-y-3 max-sm:grid-cols-1">
      {v.option === 'time' && <TimeField form={form} label={timeLabel} hint={timeHint} />}
      {v.option === 'hours' && (
        <div>
          <label htmlFor={hoursId} className="mb-1.5 block text-[13px] font-medium text-ink">
            {hoursLabel}
          </label>
          <input
            id={hoursId}
            className={FIELD}
            inputMode={form.format?.kind === 'hmm' ? 'text' : 'decimal'}
            autoComplete="off"
            data-answer-input
            value={v.hours}
            aria-invalid={form.hoursError ? true : undefined}
            aria-describedby={`${hoursId}-hint`}
            onChange={(e) => form.setHours(e.target.value)}
          />
          <span
            id={`${hoursId}-hint`}
            className={`mt-1 block text-[12px] ${form.hoursError ? 'text-bad' : 'text-ink-3'}`}
          >
            {form.hoursError ?? hoursHint(form.format)}
          </span>
        </div>
      )}
      <div className={v.option === 'hours' || v.option === 'time' ? '' : 'col-span-full'}>
        <label htmlFor={noteId} className="mb-1.5 block text-[13px] font-medium text-ink">
          Пояснение (необязательно)
        </label>
        <textarea
          id={noteId}
          className={`${textareaCls} h-16 resize-y`}
          value={v.note}
          maxLength={300}
          placeholder={notePlaceholder}
          onChange={(e) => form.setNote(e.target.value)}
        />
      </div>
    </div>
  );
}

const BOX = 'flex flex-col gap-1.5 rounded-control border px-3.5 py-3 text-[13px]';
const CAPTION = 'text-[11px] font-semibold tracking-[0.06em] text-ink-3 uppercase';

/** Ответ без поля — что будет; ввод не дописан — что сделать. Иначе — `null`. */
function waitText(form: AnswerForm, many: boolean): ReactNode {
  const v = form.values;
  if (!v.option) return 'Выберите ответ.';
  if (v.option === 'later') {
    return (
      <>
        <span className={CAPTION}>Что будет</span>
        <span>
          {many ? 'Вопросы останутся без ответа и в счётчике.' : 'Вопрос останется без ответа и в счётчике.'}
        </span>
      </>
    );
  }
  if (v.option === 'time' && !v.time.trim()) {
    return many
      ? 'Укажите время — здесь появится расчёт по каждому.'
      : 'Укажите время — здесь появится расчёт.';
  }
  if (v.option === 'time' && form.timeParsed && 'error' in form.timeParsed) return null;
  if (v.option === 'hours' && !v.hours.trim()) {
    return many
      ? 'Укажите часы — здесь появится расчёт по каждому.'
      : 'Укажите часы — здесь появится расчёт.';
  }
  if (form.hoursError) return null;
  return undefined;
}

/**
 * Строки «как посчитано» от сервера чеком: подпись слева, значение справа («Обед (на объекте больше 4 ч): − 1 ч»).
 * Ответ временем — строки расчёта «что если», в том числе «указано бригадиром».
 */
export function PreviewReceipt({ lines }: { lines: readonly string[] }) {
  return (
    <ul aria-label="Как посчитано по ответу" className="flex flex-col divide-y divide-line">
      {lines.map((l, i) => {
        const at = l.lastIndexOf(': ');
        const label = at > 0 ? l.slice(0, at) : l;
        const value = at > 0 ? l.slice(at + 2) : '';
        // Строки чека — по порядку и могут повториться: ключ — место и текст.
        return (
          <li key={`${i}-${l}`} className="flex items-baseline justify-between gap-3 py-1 text-ink-2">
            <span className="min-w-0">{label}</span>
            {value && <span className="shrink-0 text-right text-ink tabular-nums">{value}</span>}
          </li>
        );
      })}
    </ul>
  );
}

/** Ошибка запроса предпросмотра словами и «Повторить». */
function PreviewFail({ error, retry }: { error: unknown; retry: () => void }) {
  const e = error instanceof ApiError ? error : null;
  const text = errorText(error);
  return (
    <div className={`${BOX} border-transparent bg-bad-soft text-bad`}>
      <span className="flex items-start gap-2">
        <CircleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
        <span>
          Не удалось посчитать: {text}.{e?.requestId ? ` Код ошибки: ${e.requestId}.` : ''}
        </span>
      </span>
      <span>
        <Button size="sm" className="max-tablet:min-h-10" onClick={retry}>
          Повторить
        </Button>
      </span>
    </div>
  );
}

/** Предупреждение — не блокирует сохранение. */
function Warn({ children }: { children: ReactNode }) {
  return (
    <span className="flex items-start gap-2 rounded-[6px] bg-warn-soft px-2.5 py-1.5 text-ink">
      <TriangleAlert size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-warn" aria-hidden />
      <span>{children}</span>
    </span>
  );
}

/** Часы ответа: значение правил объекта, иначе число. */
const shown = (r: PreviewResult, f: HoursFormat | null) =>
  r.value ?? (r.hours != null ? hoursText(r.hours, f) : '—');

/** Ответ меняет часы — правка бригадира; «да, верно» — часы не меняются. */
const changesHours = (o: AnswerOption | null) => o === 'hours' || o === 'zero' || o === 'time';

function EditChip({ edit }: { edit: boolean }) {
  return edit ? (
    <span className="inline-flex h-[22px] items-center rounded-full bg-edit px-2 text-[12px] font-medium text-warn">
      ✎ правка бригадира
    </span>
  ) : (
    <span className="inline-flex h-[22px] items-center rounded-full bg-ok-soft px-2 text-[12px] font-medium text-ok">
      ✓ часы не меняются
    </span>
  );
}

/** «В табель попадёт» для одного вопроса (FR-018): часы, как получены, предупреждения, комментарий правки. */
function PreviewBox({ form, questionId }: { form: AnswerForm; questionId: number }) {
  const wait = waitText(form, false);
  let body: ReactNode;
  if (wait === null) body = null;
  else if (wait !== undefined) body = <div className={`${BOX} border-line bg-subtle text-ink-2`}>{wait}</div>;
  else if (form.preview.status === 'fail')
    body = <PreviewFail error={form.preview.error} retry={form.retryPreview} />;
  else if (form.preview.status !== 'ok')
    body = <div className={`${BOX} border-line bg-subtle text-ink-3`}>Считаем…</div>;
  else {
    const r = form.preview.results.find((x) => x.questionId === questionId);
    if (!r) body = null;
    else if (!r.ok)
      body = (
        <div className={`${BOX} flex-row items-start gap-2 border-transparent bg-bad-soft text-bad`}>
          <CircleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
          <span>{r.error?.message ?? 'Ответ не подходит. Проверьте ввод.'}</span>
        </div>
      );
    else {
      const edit = changesHours(form.values.option);
      body = (
        <div className={`${BOX} border-line bg-surface`}>
          <span className={CAPTION}>В табель попадёт</span>
          <span className="flex flex-wrap items-center gap-2.5">
            <span className="text-[22px] leading-[1.1] font-semibold tracking-[-0.01em] tabular-nums">
              {shown(r, form.format)} ч
            </span>
            <EditChip edit={edit} />
          </span>
          {r.lines && r.lines.length > 0 && (
            <>
              <span className={`${CAPTION} mt-1`}>Как посчитано</span>
              <PreviewReceipt lines={r.lines} />
            </>
          )}
          {(r.warnings ?? []).map((w) => (
            <Warn key={w}>{warningText(w)}</Warn>
          ))}
          {r.comment && (
            <>
              <span className={`${CAPTION} mt-1`}>{edit ? 'Комментарий правки' : 'Запись в журнале'}</span>
              <span className="rounded-[6px] bg-subtle px-2.5 py-1.5 text-ink">{r.comment}</span>
            </>
          )}
        </div>
      );
    }
  }
  return (
    <div aria-live="polite" data-preview>
      {body}
    </div>
  );
}

/* ---------- Один вопрос: ответ бригадира ---------- */

export interface AnswerDraftApi extends AnswerForm {
  question: Question;
  /** Ввод на экране (вопрос можно редактировать). */
  active: boolean;
  /** Сохранить можно. */
  canSave: boolean;
  busy: boolean;
  /** Сервер ответил «вопрос изменился»: введённое осталось, повтор — с новой версией. */
  stale: boolean;
  /** Сохранить. `viaGuard` — из окна «Сохранить ответ?»: дальше по очереди не идти. */
  submit: (opts?: { viaGuard?: boolean }) => Promise<void>;
}

/**
 * Ответ на один вопрос дня: ввод, предпросмотр, запись (`POST …/questions/answer`), несохранённое. `onSaved` —
 * ответ записан (страница перечитывает табель). `QUESTION_GONE` — тост и перечитать; `STALE_STATE` — «вопрос
 * изменился» над вводом, введённое остаётся; другие ошибки — тост с «Повторить».
 */
export function useAnswerDraft({
  timesheetId,
  question,
  displayName,
  format,
  tzOffset,
  active,
  onSaved,
  onStale,
}: {
  timesheetId: number;
  question: Question | null;
  displayName: string;
  format: HoursFormat | null;
  /** Часы объекта: прежнее время бригадира в поле — по ним. */
  tzOffset?: string;
  /** Ввод на экране. */
  active: boolean;
  onSaved: (res: AnswerResponse, answer: AnswerInput, opts: { advance: boolean }) => void;
  /** Вопрос снят или изменился: текущие вопросы из `409 STALE_STATE` (`details.current`) — страница их покажет. */
  onStale?: (current: Question[]) => void;
}): AnswerDraftApi | null {
  const { showError } = useToast();
  const { clearDirty } = useUnsavedGuard();
  const [busy, setBusy] = useState(false);
  const q = question;
  const on = active && q !== null;
  const initial: DraftValues = q
    ? initialValues(q, format, tzOffset)
    : { option: null, hours: '', time: '', note: '' };
  const form = useAnswerForm({
    timesheetId,
    formKey: q ? `${q.id}` : '',
    items: q ? [{ questionId: q.id, version: q.version }] : [],
    initial,
    format,
    enabled: on,
  });
  const stale = form.state?.stale ?? false;
  const canSave = on && form.ready && !busy;

  const submit = async (opts?: { viaGuard?: boolean }) => {
    const req = form.request;
    if (!q || !req || !(opts?.viaGuard ? form.complete : form.ready)) throw new Error('Ответ не дописан');
    const version = form.state?.stale ? q.version : (form.state?.baseVersion ?? q.version);
    setBusy(true);
    try {
      const res = await api.answer(timesheetId, { items: [{ questionId: q.id, version }], answer: req });
      form.reset();
      clearDirty();
      onSaved(res, req, { advance: !opts?.viaGuard });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'STALE_STATE') {
        form.setState((x) => ({
          key: `${q.id}`,
          values: x?.values ?? form.values,
          init: x?.init ?? initial,
          baseVersion: x?.baseVersion ?? q.version,
          stale: true,
        }));
        onStale?.(staleQuestions(e.details));
      } else if (e instanceof ApiError && e.code === 'QUESTION_GONE') {
        showError(e, { screen: SCREEN, action: 'answer' });
        form.reset();
        clearDirty();
        onStale?.([]);
      } else {
        showError(e, {
          screen: SCREEN,
          action: 'answer',
          lead: 'Не сохранено',
          note: 'Введённое осталось в панели.',
          retry: () => void latest.current().catch(() => {}),
        });
      }
      throw e;
    } finally {
      setBusy(false);
    }
  };
  const latest = useRef(submit);
  latest.current = submit;

  const option = q?.options.find((o) => o.key === form.values.option);
  const tp = form.timeParsed;
  const hours =
    form.values.option === 'hours' && form.values.hours.trim()
      ? ` ${form.values.hours.trim()} ч`
      : tp && 'time' in tp
        ? ` ${tp.time}`
        : '';
  useUnsavedChanges(
    on && q && form.dirty
      ? {
          kind: 'answer',
          summary: `${displayName}, ${ruDate(q.date).slice(0, 5)}: ${typeLabel(q.type)} — ${option ? option.label.replace(/\s*…\s*$/, '') : 'ответ не выбран'}${hours}`,
          canSave: form.complete,
          save: () => latest.current({ viaGuard: true }),
          discard: form.reset,
        }
      : null,
  );

  if (!q) return null;
  return { ...form, question: q, active: on, canSave, busy, stale, submit };
}

/* ---------- Решение кадров ---------- */

export interface ReviewApi {
  /** Открыт ввод «Уточнить». */
  clarifying: boolean;
  comment: string;
  setComment: (v: string) => void;
  busy: boolean;
  accept: () => void;
  start: () => void;
  cancel: () => void;
  submit: () => Promise<void>;
}

/**
 * Кадры: «Принято» сразу, «Уточнить» — с обязательным комментарием до 500 знаков (FR-029). Комментарий —
 * несохранённое («Сохранить «Уточнить»?»).
 */
export function useReview({
  timesheetId,
  question,
  displayName,
  onReviewed,
  onStale,
}: {
  timesheetId: number;
  question: Question | null;
  displayName: string;
  onReviewed?: (q: Question) => void;
  /** Ответ изменился, пока кадры смотрели (`409 STALE_STATE`), или вопрос снят: текущие вопросы — странице. */
  onStale?: (current: Question[]) => void;
}): ReviewApi {
  const { showError, showInfo } = useToast();
  const [state, setState] = useState<{ qid: number; comment: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const q = question;
  const s = state && q && state.qid === q.id ? state : null;

  const send = async (decision: 'accepted' | 'clarify', comment?: string) => {
    if (!q) return;
    setBusy(true);
    try {
      const res = await api.review(timesheetId, q.id, {
        decision,
        ...(comment ? { comment } : {}),
        version: q.version,
      });
      setState(null);
      onReviewed?.(res.question);
      showInfo(decision === 'accepted' ? 'Ответ принят' : 'Отмечено «Уточнить»');
    } catch (e) {
      // Ответ бригадира изменился или вопрос снят: повторять то же решение нельзя — показать новое состояние.
      const changed = e instanceof ApiError && (e.code === 'STALE_STATE' || e.code === 'QUESTION_GONE');
      showError(e, {
        screen: SCREEN,
        action: 'review',
        lead: 'Не сохранено',
        ...(changed
          ? { note: 'Проверьте ответ ещё раз.' }
          : decision === 'clarify'
            ? { note: 'Комментарий остался в панели.' }
            : {}),
        retry: changed ? undefined : () => void send(decision, comment).catch(() => {}),
      });
      if (changed) onStale?.(e instanceof ApiError ? staleQuestions(e.details) : []);
      throw e;
    } finally {
      setBusy(false);
    }
  };
  const submit = async () => {
    const c = s?.comment.trim();
    if (!c) throw new Error('Комментарий не дописан');
    await send('clarify', c);
  };
  const latest = useRef(submit);
  latest.current = submit;

  useUnsavedChanges(
    s && q && s.comment.trim()
      ? {
          kind: 'clarify',
          summary: `${displayName}, ${ruDate(q.date).slice(0, 5)}: «${s.comment.trim()}»`,
          canSave: true,
          save: () => latest.current(),
          discard: () => setState(null),
        }
      : null,
  );

  return {
    clarifying: s !== null,
    comment: s?.comment ?? '',
    setComment: (v) => setState((x) => (x && q && x.qid === q.id ? { ...x, comment: v } : x)),
    busy,
    accept: () => void send('accepted').catch(() => {}),
    start: () => q && setState({ qid: q.id, comment: '' }),
    cancel: () => setState(null),
    submit,
  };
}

/* ---------- Блок вопроса ---------- */

const stamp = (iso: string, tzOffset: string) => {
  const full = ruDateTime(iso, tzOffset);
  return `${full.slice(0, 5)} ${full.slice(11)}`;
};

export type StripTone = 'neutral' | 'warn' | 'bad' | 'ok';

/** Полоса состояния вопроса под шапкой карточки (contracts/ui-panel.md, «Карточка дня: состав», п. 1). */
export function questionStrip(
  q: Question,
  tzOffset: string,
): { tone: StripTone; icon: typeof Clock; content: ReactNode } {
  const a = q.answer;
  const r = q.review;
  switch (q.state) {
    case 'answered':
      return a?.changedAfterAccept
        ? {
            tone: 'warn',
            icon: PenLine,
            content: (
              <>
                <b className="font-semibold">Изменён после принятия</b> {stamp(a.at, tzOffset)}
                {a.by ? ` · ${a.by}` : ''}
              </>
            ),
          }
        : {
            tone: 'ok',
            icon: Check,
            content: (
              <>
                <b className="font-semibold">Отвечен</b>
                {a ? ` ${stamp(a.at, tzOffset)}${a.by ? ` · ${a.by}` : ''}` : ''}
              </>
            ),
          };
    case 'accepted':
      return {
        tone: 'ok',
        icon: CircleCheck,
        content: (
          <>
            <b className="font-semibold">Принят кадрами</b>
            {r ? ` ${stamp(r.at, tzOffset)}${r.by ? ` · ${r.by}` : ''}` : ''}
          </>
        ),
      };
    case 'clarify':
      return {
        tone: 'warn',
        icon: Undo2,
        content: (
          <>
            <b className="font-semibold">Кадры просят уточнить</b>
            {r ? ` · ${stamp(r.at, tzOffset)}` : ''}
          </>
        ),
      };
    case 'later':
      return {
        tone: 'neutral',
        icon: Clock,
        content: (
          <>
            <b className="font-semibold">Отложен</b>
            {a ? ` ${stamp(a.at, tzOffset)}` : ''}
            {a?.note ? ` · ${a.note}` : ''}
          </>
        ),
      };
    default:
      if (q.old) {
        return {
          tone: 'bad',
          icon: Clock,
          content: (
            <>
              <b className="font-semibold">
                Без ответа {q.daysWithoutAnswer ?? ''}{' '}
                {plural(q.daysWithoutAnswer ?? 0, ['день', 'дня', 'дней'])}
              </b>{' '}
              — давно без ответа
            </>
          ),
        };
      }
      return {
        tone: 'warn',
        icon: MessageCircleQuestion,
        content: (
          <>
            <b className="font-semibold">Без ответа</b>
            {q.isNew ? ' · новый, появился за вчера' : ''}
          </>
        ),
      };
  }
}

/** Значки состояния у заголовка блока и в списке вопросов. */
export function StateBadges({ q }: { q: Question }) {
  return (
    <>
      {q.state === 'open' &&
        (q.old ? (
          <Badge tone="bad">
            {q.daysWithoutAnswer ?? ''} {plural(q.daysWithoutAnswer ?? 0, ['день', 'дня', 'дней'])} без ответа
          </Badge>
        ) : (
          <Badge tone="warn">Без ответа</Badge>
        ))}
      {q.state === 'later' && <Badge>Уточню позже</Badge>}
      {q.state === 'clarify' && <Badge tone="warn">Кадры: уточнить</Badge>}
      {q.state === 'answered' && <Badge tone="ok">Отвечен</Badge>}
      {q.state === 'accepted' && <Badge tone="ok">Принят</Badge>}
      {q.answer?.changedAfterAccept && <Badge tone="warn">изменён после принятия</Badge>}
      {q.isNew && isUnanswered(q) && <Badge tone="accent">новый</Badge>}
    </>
  );
}

/** Ответ бригадира только для чтения: что ответил, пояснение, кто и когда; решение кадров. */
function AnswerView({ q, format, tzOffset }: { q: Question; format: HoursFormat | null; tzOffset: string }) {
  const a = q.answer;
  if (!a) return null;
  return (
    <div data-answer className={`${BOX} border-line bg-surface`}>
      <span className={CAPTION}>Ответ бригадира</span>
      <span className="flex flex-wrap items-center gap-2">
        <b className="font-semibold text-ink">{answerSummary(a, format, tzOffset)}</b>
        {a.option !== 'later' && <EditChip edit={changesHours(a.option) || a.via === 'edit'} />}
        {a.changedAfterAccept && <Badge tone="warn">изменён после принятия</Badge>}
      </span>
      {a.note && <span className="text-ink-2">Пояснение: «{a.note}»</span>}
      <span className="text-[12.5px] text-ink-3">
        {[a.by, stamp(a.at, tzOffset)].filter(Boolean).join(' · ')}
      </span>
      {q.review?.decision === 'accepted' && (
        <span className="text-[12.5px] text-ok">
          Кадры: принято · {[q.review.by, stamp(q.review.at, tzOffset)].filter(Boolean).join(' · ')}
        </span>
      )}
    </div>
  );
}

export interface QuestionBlockProps {
  q: Question;
  expanded: boolean;
  /** Один вопрос дня: заголовок не сворачивается. */
  single: boolean;
  onExpand: () => void;
  /** Ввод ответа — у раскрытого вопроса, который бригадир может менять. */
  draft: AnswerDraftApi | null;
  /** Ввод «Уточнить» — у кадров. */
  review: ReviewApi | null;
  /** Без права менять: почему. */
  readOnlyText: string | null;
  keys: boolean;
  format: HoursFormat | null;
  tzOffset: string;
}

/**
 * Блок одного вопроса: заголовок (тип и состояние; свёрнутый — ответ коротко), текст вопроса, комментарий кадров,
 * ввод ответа с предпросмотром или ответ только для чтения, ввод «Уточнить» у кадров.
 */
export function QuestionBlock({
  q,
  expanded,
  single,
  onExpand,
  draft,
  review,
  readOnlyText,
  keys,
  format,
  tzOffset,
}: QuestionBlockProps) {
  const clarifyId = useId();
  const head = (
    <>
      <Badge tone={typeTone(q.type)}>{typeLabel(q.type)}</Badge>
      <StateBadges q={q} />
      <span className="flex-1" aria-hidden />
      {!expanded && q.answer && (
        <span className="min-w-0 truncate text-[12.5px] text-ink-2">
          {answerSummary(q.answer, format, tzOffset)}
        </span>
      )}
    </>
  );
  const editing = draft?.active && draft.question.id === q.id ? draft : null;
  return (
    <div
      data-question-block={q.id}
      className={`rounded-card border bg-surface ${expanded ? 'border-line-strong shadow-card' : 'border-line'}`}
    >
      {single ? (
        <div className="flex min-h-10 flex-wrap items-center gap-1.5 px-3 py-2.5">{head}</div>
      ) : (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={expanded ? undefined : onExpand}
          className="flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-card px-3 py-2.5 text-left hover:bg-subtle"
        >
          {head}
          {expanded ? (
            <ChevronDown size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />
          ) : (
            <ChevronRight size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />
          )}
        </button>
      )}
      {expanded && (
        <div className="flex flex-col gap-3 px-3 pb-3">
          <p className="text-[14px] leading-[1.45] text-ink">{questionText(q)}</p>
          {q.state === 'clarify' && q.review?.comment && (
            <Callout tone="warn" icon={Undo2}>
              <p>Кадры: «{q.review.comment}»</p>
              {/* Бригадир отвечает снова — прежний ответ коротко; кадрам и без ввода — ответ целиком ниже (T112). */}
              {q.answer && editing && <p>Ваш ответ был: {answerSummary(q.answer, format, tzOffset)}.</p>}
            </Callout>
          )}
          {editing ? (
            <>
              {editing.stale && (
                <Callout tone="warn" icon={RefreshCw}>
                  <p className="font-semibold">Вопрос изменился, пока была открыта форма</p>
                  <p className="mt-0.5">Введённое осталось. Проверьте и сохраните ещё раз.</p>
                </Callout>
              )}
              <AnswerOptions
                options={answerOptions(q)}
                value={editing.values.option}
                onChange={editing.setOption}
                keys={keys}
              />
              {editing.values.option && <AnswerFields form={editing} timeLabel={timeLabelOf(q.type)} />}
              <PreviewBox form={editing} questionId={q.id} />
            </>
          ) : q.answer ? (
            <AnswerView q={q} format={format} tzOffset={tzOffset} />
          ) : readOnlyText ? (
            <p className={`${BOX} border-line bg-subtle text-ink-2`}>{readOnlyText}</p>
          ) : null}
          {review?.clarifying && (
            <div>
              <label htmlFor={clarifyId} className="mb-1.5 block text-[13px] font-medium text-ink">
                Что уточнить (обязательно)
              </label>
              <textarea
                id={clarifyId}
                className={`${textareaCls} h-20 resize-y`}
                value={review.comment}
                maxLength={500}
                placeholder="Например: в журнале охраны выход в 18:20"
                onChange={(e) => review.setComment(e.target.value)}
              />
              <span className="mt-1 block text-[12px] text-ink-3">
                Вопрос станет у бригадира снова без ответа после «Вернуть бригадиру».
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Подсказка клавиш в подвале (компьютер). */
function KeysHint() {
  const k =
    'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-xs border border-b-[1.5px] border-line bg-surface px-1 text-[11px] font-medium text-ink-2';
  return (
    <span className="inline-flex items-center gap-1 text-[12px] whitespace-nowrap text-ink-3">
      <kbd className={k}>1</kbd>–<kbd className={k}>4</kbd> вариант <kbd className={k}>↵</kbd> сохранить
    </span>
  );
}

/**
 * Почему ответ ещё нельзя сохранить — коротко, для подвала рядом с неактивной кнопкой; можно — `null`. На стенде
 * 14.10.2026 вариант «Выход в …» был выбран сам, поле времени пустое, а подсказка «Укажите время» ушла под подвал:
 * кнопка не работала без объяснения. Полный текст отказа сервера — в блоке расчёта выше.
 */
export function answerBlockReason(form: AnswerForm, timeLabel: string): string | null {
  if (form.ready) return null;
  const v = form.values;
  if (!v.option) return 'Выберите вариант ответа';
  if (v.option === 'time') {
    if (!v.time.trim()) return `Укажите ${timeLabel.charAt(0).toLowerCase()}${timeLabel.slice(1)}`;
    if (form.timeParsed && 'error' in form.timeParsed) return 'Исправьте время';
  }
  if (v.option === 'hours') {
    if (!v.hours.trim()) return 'Укажите часы';
    if (form.hoursError) return 'Исправьте часы';
  }
  if (form.preview.status === 'fail') return 'Часы не посчитаны — повторите расчёт';
  if (form.preview.status === 'ok') return 'Ответ не подходит — причина выше';
  return 'Система считает часы…';
}

/** Причина в подвале: вместо подсказки клавиш и «Не сохранено» — главное, что мешает сохранить. */
export function BlockReason({ text }: { text: string }) {
  return (
    <span aria-live="polite" data-block-reason className="min-w-0 text-[13px] font-medium text-ink-2">
      {text}
    </span>
  );
}

/** Подвал при вводе ответа: «Отменить изменение» · причина / «Не сохранено» / клавиши · «Сохранить ответ». */
export function AnswerFooter({
  draft,
  label,
  keys,
  onCancelEdit,
}: {
  draft: AnswerDraftApi;
  label: string;
  keys: boolean;
  onCancelEdit?: () => void;
}) {
  const reason =
    draft.active && !draft.busy ? answerBlockReason(draft, timeLabelOf(draft.question.type)) : null;
  return (
    <>
      {onCancelEdit && (
        <Button variant="ghost" disabled={draft.busy} onClick={onCancelEdit}>
          Отменить изменение
        </Button>
      )}
      <span className="flex-1" aria-hidden />
      {/* В подвале узкой панели место на одно: почему нельзя сохранить, «Не сохранено» или подсказка клавиш. */}
      {reason ? <BlockReason text={reason} /> : draft.dirty ? <UnsavedMark /> : keys && <KeysHint />}
      <Button variant="primary" disabled={!draft.canSave} onClick={() => void draft.submit().catch(() => {})}>
        {label}
      </Button>
    </>
  );
}

/**
 * Подвал кадров: «Уточнить» · «Принято»; принятый ответ — только «Уточнить» (`canAccept: false`, T111); при вводе
 * комментария — «Отмена» · «Отметить «Уточнить»».
 */
export function ReviewFooter({
  review,
  extra,
  canAccept = true,
}: { review: ReviewApi; extra?: ReactNode; canAccept?: boolean }) {
  if (review.clarifying) {
    return (
      <>
        <span className="flex-1" aria-hidden />
        {review.comment.trim() && <UnsavedMark />}
        <Button disabled={review.busy} onClick={review.cancel}>
          Отмена
        </Button>
        <Button
          variant="primary"
          disabled={!review.comment.trim() || review.busy}
          onClick={() => void review.submit().catch(() => {})}
        >
          Отметить «Уточнить»
        </Button>
      </>
    );
  }
  return (
    <>
      {extra}
      <Button icon={Undo2} disabled={review.busy} onClick={review.start}>
        Уточнить
      </Button>
      <span className="flex-1" aria-hidden />
      {canAccept && (
        <Button variant="primary" icon={Check} disabled={review.busy} onClick={review.accept}>
          Принято
        </Button>
      )}
    </>
  );
}
